import { useEffect, useMemo, useState, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Settings } from 'lucide-react';
import { api } from '../lib/api';
import { ReviewList } from '../components/reviews/ReviewList';
import { ReviewDetailDrawer } from '../components/reviews/ReviewDetailDrawer';
import { CostBanner } from '../components/reviews/CostBanner';
import {
  ReviewFilterBar,
  filterReviews,
  type ReviewFilter,
} from '../components/reviews/ReviewFilterBar';
import type { ReviewSummary, ReviewDetail as ReviewDetailType, ReviewContextData } from '../components/reviews/types';

type ConnectionStatus = 'connected' | 'disconnected' | 'reconnecting';

export function Reviews() {
  const navigate = useNavigate();
  const [reviews, setReviews] = useState<ReviewSummary[]>([]);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [detail, setDetail] = useState<ReviewDetailType | null>(null);
  const [context, setContext] = useState<ReviewContextData | null | 'loading'>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [connectionStatus, setConnectionStatus] = useState<ConnectionStatus>('disconnected');
  const [filter, setFilter] = useState<ReviewFilter>('active');
  const [search, setSearch] = useState('');
  const detailAbortRef = useRef<AbortController | null>(null);

  const fetchReviews = useCallback(async () => {
    try {
      const data = await api.reviews.list();
      setReviews(data.reviews ?? []);
      setError(null);
      setConnectionStatus('connected');
    } catch {
      setError('Failed to load reviews');
      setConnectionStatus('disconnected');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchReviews();
    // Polling fallback until WebSocket observer is available
    const interval = setInterval(fetchReviews, 10_000);
    return () => clearInterval(interval);
  }, [fetchReviews]);

  const handleRetry = useCallback(async (sessionId: string, fromStep: string) => {
    try {
      await api.reviews.retry(sessionId, fromStep);
      // Trigger a fresh list fetch so the retried review's state updates
      fetchReviews();
    } catch (err) {
      setError(`Retry failed: ${err instanceof Error ? err.message : String(err)}`);
    }
  }, [fetchReviews]);

  const handleSelect = useCallback(async (sessionId: string) => {
    detailAbortRef.current?.abort();
    const controller = new AbortController();
    detailAbortRef.current = controller;

    setSelectedId(sessionId);
    setContext('loading');
    try {
      const epoch = reviews.find((r) => r.sessionId === sessionId)?.epoch;
      // Fetch session detail and context breakdown in parallel
      const [data, contextData] = await Promise.allSettled([
        api.reviews.get(sessionId),
        api.reviews.context(sessionId, epoch !== undefined ? { epoch } : undefined),
      ]);

      if (controller.signal.aborted) return;

      if (data.status === 'fulfilled') {
        setDetail(data.value);
      } else {
        if (data.reason instanceof DOMException && data.reason.name === 'AbortError') { setDetail(null); setContext(null); return; }
        setDetail(null);
      }

      // Context is best-effort — 404 = pre-Phase19 or TTL-expired, treat as null
      setContext(contextData.status === 'fulfilled' ? contextData.value : null);
    } catch (err) {
      if (err instanceof DOMException && err.name === 'AbortError') { setDetail(null); setContext(null); return; }
      setDetail(null);
      setContext(null);
    }
  }, [reviews]);

  const handleCloseDrawer = useCallback(() => {
    detailAbortRef.current?.abort();
    setSelectedId(null);
    setDetail(null);
    setContext(null);
  }, []);

  const filteredReviews = useMemo(
    () => filterReviews(reviews, filter, search),
    [reviews, filter, search],
  );

  const statusColor: Record<ConnectionStatus, string> = {
    connected: 'text-green-400',
    disconnected: 'text-gray-500',
    reconnecting: 'text-yellow-400',
  };

  const statusLabel: Record<ConnectionStatus, string> = {
    connected: 'Live',
    disconnected: 'Offline',
    reconnecting: 'Reconnecting...',
  };

  return (
    <div className="min-h-screen bg-gray-950 text-white">
      <div className="max-w-7xl mx-auto px-6 py-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-6 gap-4">
          <div className="flex items-center gap-4 min-w-0">
            <button
              onClick={() => navigate('/dashboard')}
              className="p-2 rounded-lg hover:bg-gray-800 transition-colors text-gray-400 hover:text-white shrink-0"
              title="Back to dashboard"
            >
              <ArrowLeft className="w-5 h-5" />
            </button>
            <h1 className="text-2xl font-bold truncate">Review Command Centre</h1>
          </div>
          <div className="flex items-center gap-3 shrink-0">
            <button
              onClick={() => navigate('/reviews/settings')}
              className="p-2 rounded-lg hover:bg-gray-800 transition-colors text-gray-400 hover:text-white"
              title="Review Settings"
            >
              <Settings className="w-5 h-5" />
            </button>
            <CostBanner compact />
            <span
              data-testid="connection-status"
              className={`inline-flex items-center gap-2 text-sm ${statusColor[connectionStatus]}`}
            >
              <span className="w-2 h-2 rounded-full bg-current" />
              {statusLabel[connectionStatus]}
            </span>
          </div>
        </div>

        {/* Loading */}
        {loading && (
          <div data-testid="loading-indicator" className="flex justify-center py-12">
            <div className="animate-spin w-8 h-8 border-2 border-teal-400 border-t-transparent rounded-full" />
          </div>
        )}

        {/* Error */}
        {error && !loading && (
          <div className="bg-red-900/30 border border-red-800 rounded-lg p-4 mb-6 text-red-300">
            {error}
          </div>
        )}

        {/* Content */}
        {!loading && !error && (
          <>
            <ReviewFilterBar
              filter={filter}
              onFilterChange={setFilter}
              search={search}
              onSearchChange={setSearch}
              totalCount={reviews.length}
              visibleCount={filteredReviews.length}
            />
            <ReviewList reviews={filteredReviews} onSelect={handleSelect} onRetry={handleRetry} />
          </>
        )}

        <ReviewDetailDrawer
          open={!!selectedId}
          onClose={handleCloseDrawer}
          detail={detail}
          context={context}
        />
      </div>
    </div>
  );
}
